<template>
    <div class="fill-panel">
        <header class="fill-panel__header">
            <div class="fill-panel__tabs">
                <button class="fill-panel__tab fill-panel__tab--active">Custom</button>
                <button class="fill-panel__tab">Libraries</button>
            </div>
            <div class="fill-panel__header-actions">
                <button class="icon-btn" aria-label="Add style">+</button>
                <button class="icon-btn" aria-label="More options">×</button>
            </div>
        </header>

        <section class="fill-panel__modes" aria-label="Fill type">
            <button class="mode-btn mode-btn--active" aria-label="Solid fill">
                <span class="mode-icon solid"></span>
            </button>
            <button class="mode-btn" aria-label="Linear gradient">
                <span class="mode-icon linear"></span>
            </button>
            <button class="mode-btn" aria-label="Radial gradient">
                <span class="mode-icon radial"></span>
            </button>
            <button class="mode-btn" aria-label="Angular gradient">
                <span class="mode-icon angular"></span>
            </button>
            <button class="mode-btn" aria-label="Image fill">
                <span class="mode-icon image"></span>
            </button>
            <button class="mode-btn" aria-label="Drop shadow">
                <span class="mode-icon shadow"></span>
            </button>
        </section>

        <section class="fill-panel__picker" aria-label="Color picker">
            <div class="picker-area" :style="{ '--picker-color': currentColor }">
                <div class="picker-area__gradient"></div>
                <div class="picker-area__thumb"></div>
            </div>

            <div class="picker-strip picker-strip--hue">
                <div class="picker-strip__gradient"></div>
                <div class="picker-strip__thumb"></div>
            </div>

            <div class="picker-strip picker-strip--alpha">
                <div class="picker-strip__checkerboard"></div>
                <div class="picker-strip__alpha" :style="{ '--picker-color': currentColor }"></div>
                <div class="picker-strip__thumb"></div>
            </div>
        </section>

        <section class="fill-panel__inputs">
            <div class="fill-panel__format">
                <button class="format-btn">Hex</button>
            </div>
            <input
                type="text"
                class="fill-panel__hex"
                v-model="hexValue"
                spellcheck="false"
            />
            <div class="fill-panel__opacity">
                <input
                    type="number"
                    min="0"
                    max="100"
                    v-model.number="opacityValue"
                />
                <span>%</span>
            </div>
        </section>

        <section class="fill-panel__palette">
            <div class="fill-panel__palette-header">
                <span>On this page</span>
                <button class="palette-toggle">
                    <span>▼</span>
                </button>
            </div>
            <div class="fill-panel__swatches">
                <button
                    v-for="(swatch, index) in swatches"
                    :key="index"
                    class="swatch"
                    :style="{ background: swatch }"
                    @click="setFromSwatch(swatch)"
                />
            </div>
        </section>

        <!-- Hidden native input keeps behavior simple and accessible -->
        <input
            type="color"
            class="native-color-input"
            v-model="hexValue"
            aria-hidden="true"
            tabindex="-1"
        />
    </div>
</template>

<script>
const DEFAULT_HEX = '#C4C4C4';

export default {
    name: 'FillMenuComp',
    props: {
        fill: {
            type: Object,
            required: false,
        },
    },
    computed: {
        currentColor() {
            return this.fill?.color || DEFAULT_HEX;
        },
        hexValue: {
            get() {
                return this.currentColor;
            },
            set(value) {
                if (!this.fill) return;
                this.$set(this.fill, 'color', this.normalizeHex(value));
            },
        },
        opacityValue: {
            get() {
                if (!this.fill) return 100;
                return typeof this.fill.opacity === 'number' ? this.fill.opacity : 100;
            },
            set(value) {
                if (!this.fill) return;
                let next = typeof value === 'number' ? value : Number(value);
                if (!Number.isFinite(next)) next = 100;
                next = Math.max(0, Math.min(100, next));
                this.$set(this.fill, 'opacity', next);
            },
        },
        swatches() {
            return ['#000000', '#FFFFFF', '#C4C4C4', '#FF0000', '#00FF00', '#0000FF'];
        },
    },
    methods: {
        normalizeHex(value) {
            if (typeof value !== 'string') {
                return this.currentColor;
            }
            let hex = value.trim();
            if (!hex.startsWith('#')) hex = `#${hex}`;
            const threeDigit = /^#[0-9a-fA-F]{3}$/;
            const sixDigit = /^#[0-9a-fA-F]{6}$/;

            if (threeDigit.test(hex)) {
                hex = `#${hex
                    .slice(1)
                    .split('')
                    .map((ch) => ch + ch)
                    .join('')}`.toUpperCase();
            } else if (sixDigit.test(hex)) {
                hex = hex.toUpperCase();
            } else {
                return this.currentColor;
            }

            return hex;
        },
        setFromSwatch(color) {
            if (!this.fill) return;
            this.$set(this.fill, 'color', color);
        },
    },
};
</script>

<style scoped>
.fill-panel {
    /* width: 260px; */
    background: #1c1c1e;
    border-radius: 16px;
    padding: 10px 10px 12px;
    color: #f5f5f7;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
    display: flex;
    flex-direction: column;
    gap: 8px;
    box-sizing: border-box;
    position: absolute;
    right: 21%;
}

.fill-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
}

.fill-panel__tabs {
    display: flex;
    gap: 4px;
    font-size: 11px;
}

.fill-panel__tab {
    border: none;
    background: transparent;
    color: rgba(245, 245, 247, 0.6);
    padding: 3px 8px;
    border-radius: 999px;
    cursor: pointer;
}

.fill-panel__tab--active {
    background: rgba(255, 255, 255, 0.12);
    color: #f5f5f7;
}

.fill-panel__header-actions {
    display: flex;
    gap: 2px;
}

.icon-btn {
    width: 18px;
    height: 18px;
    border-radius: 6px;
    border: none;
    background: rgba(255, 255, 255, 0.08);
    color: #f5f5f7;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.fill-panel__modes {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 4px;
}

.mode-btn {
    height: 26px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.03);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.mode-btn--active {
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.1);
}

.mode-icon {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    position: relative;
    overflow: hidden;
}

.mode-icon.solid {
    background: linear-gradient(135deg, #ff3b30, #ff9500);
}

.mode-icon.linear::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, #ff3b30, #ffcc00, #34c759, #0a84ff, #af52de);
}

.mode-icon.radial::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle, #ff3b30, #111);
}

.mode-icon.angular::before {
    content: '';
    position: absolute;
    inset: 0;
    background: conic-gradient(#ff3b30, #ffcc00, #34c759, #0a84ff, #af52de, #ff3b30);
}

.mode-icon.image {
    background: linear-gradient(135deg, #4a4a4a, #1f1f1f);
}

.mode-icon.image::before {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 2px;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.mode-icon.shadow::before {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: #f5f5f7;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.8);
}

.fill-panel__picker {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 4px;
}

.picker-area {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.9);
    height: 140px;
    background: #000;
}

.picker-area__gradient {
    position: absolute;
    inset: 0;
    background-image:
        linear-gradient(0deg, #000, transparent),
        linear-gradient(90deg, #fff, var(--picker-color));
}

.picker-area__thumb {
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 999px;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.5);
    top: 50%;
    left: 12%;
    transform: translate(-50%, -50%);
}

.picker-strip {
    position: relative;
    height: 16px;
    border-radius: 999px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.9);
}

.picker-strip__gradient {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        90deg,
        #ff0000,
        #ffff00,
        #00ff00,
        #00ffff,
        #0000ff,
        #ff00ff,
        #ff0000
    );
}

.picker-strip--alpha {
    margin-top: 2px;
}

.picker-strip__checkerboard {
    position: absolute;
    inset: 0;
    background-image:
        linear-gradient(45deg, #555 25%, transparent 25%),
        linear-gradient(-45deg, #555 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #555 75%),
        linear-gradient(-45deg, transparent 75%, #555 75%);
    background-size: 10px 10px;
    background-position: 0 0, 0 5px, 5px -5px, -5px 0;
    opacity: 0.5;
}

.picker-strip__alpha {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, var(--picker-color));
}

.picker-strip__thumb {
    position: absolute;
    top: 50%;
    left: 18%;
    width: 14px;
    height: 14px;
    border-radius: 999px;
    background: #f5f5f7;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.5);
    transform: translate(-50%, -50%);
}

.fill-panel__inputs {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 6px;
    align-items: center;
    margin-top: 4px;
}

.fill-panel__format {
    display: flex;
}

.format-btn {
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.05);
    color: #f5f5f7;
    font-size: 11px;
    padding: 4px 8px;
    cursor: default;
}

.fill-panel__hex {
    height: 26px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(0, 0, 0, 0.6);
    color: #f5f5f7;
    font-size: 11px;
    padding: 0 8px;
    box-sizing: border-box;
}

.fill-panel__hex:focus {
    outline: none;
    border-color: #0a84ff;
}

.fill-panel__opacity {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
}

.fill-panel__opacity input {
    width: 42px;
    height: 26px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(0, 0, 0, 0.6);
    color: #f5f5f7;
    font-size: 11px;
    padding: 0 6px;
}

.fill-panel__palette {
    margin-top: 4px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.04);
    padding: 6px 8px 8px;
}

.fill-panel__palette-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    color: rgba(245, 245, 247, 0.7);
    margin-bottom: 6px;
}

.palette-toggle {
    border: none;
    background: transparent;
    color: inherit;
    font-size: 10px;
    cursor: pointer;
    padding: 0;
}

.fill-panel__swatches {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 4px;
}

.swatch {
    height: 18px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.8);
    cursor: pointer;
}

.native-color-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
}
</style>
